import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { HrActivity, HrEmployee, HrSchedule, hrEmployeeNameOf, hrIsHrManager, hrResolveActivities, hrScheduleActivityId } from '@app/shared/_models/hr';
import { Store, User } from '@app/shared/_models/common';
import { todayStr } from '@app/shared/time';

// R8 (RGPD) — LIMITATION DE LA CONSERVATION (art. 5(1)(e)) : une fiche nominative ne se garde
// pas éternellement. La durée retenue est 5 ANS après le départ — la prescription la plus longue
// des pièces RH (action en paiement du salaire : 3 ans, L3245-1 ; registre du personnel : 5 ans
// après le départ, D1221-25) — la même que le serveur oppose à l'event d'anonymisation
// (EVENTTYP_HR_EMPLOYEE_ANONYMIZE, refusé tant qu'elle court).
//
// Dates NUES comparées en CHAÎNES (contrat maison) : aucune arithmétique dayjs sur instance tz.
// Un départ un 29 février donne un seuil « AAAA-02-29 » inexistant : l'ordre lexicographique
// reste juste (le premier jour réel qui le dépasse est le 1er mars).
export function hrAnonymizableSince(leave_date: string | null | undefined, today: string): boolean {
	if (!leave_date) return false;
	const threshold = (+leave_date.slice(0, 4) + 5) + leave_date.slice(4);
	return today > threshold;
}

// Ligne aplatie pour la table (le filtre texte natif cherche dans ce qui est affiché).
interface EmployeeRow {
	id: number;
	name: string;
	id_store: number | null;
	store: string;
	schedule: string;
	hours: string;
	mobility: string;
	active: boolean;
	// R8 — fiche sortie depuis plus de 5 ans : la conservation légale est écoulée.
	anonymizable: boolean;
	anonymizableTip: string;
}

// Liste du personnel — /hr/employees. Clic sur une ligne = fiche /hr/employee/:id.
@Component({
	selector: 'app-hr-employees',
	standalone: true,
	imports: [MenuCommonComponent, MatTableModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatCheckboxModule, MatSelectModule, MatTooltipModule],
	templateUrl: './employees.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './employees.component.scss',
})
export class EmployeesComponent implements OnInit {

	public hrService = inject(HrService);
	private commonService = inject(CommonService);
	private loginService = inject(LoginService);
	private router = inject(Router);

	public displayedColumns: string[] = ['name', 'store', 'schedule', 'hours', 'mobility', 'active'];
	public dataSource: MatTableDataSource<EmployeeRow> = new MatTableDataSource<EmployeeRow>();
	public loaded = false;
	public stores: Store[] = [];
	// Le badge « Anonymisable » ne se montre qu'au responsable RH : `leave_date` n'est de toute
	// façon servie qu'à lui (lecture expurgée), et le geste qu'il annonce est le sien.
	public isHrManager = false;
	// État des filtres (signals = state local). « Actifs » démarre coché : la liste s'ouvre sur les seuls actifs.
	public activeOnly = signal(true);
	public storeFilter = signal<number | null>(null);
	public search = signal('');

	ngOnInit(): void {
		forkJoin({
			employees: this.hrService.getEmployees$(),
			schedules: this.hrService.getSchedules$(),
			activities: this.hrService.getActivities$(),
			users: this.hrService.getUsers$(),
			stores: this.commonService.getStores(),
		}).subscribe(({ employees, schedules, activities, users, stores }) => {
			const resolved = hrResolveActivities(activities, schedules);
			this.stores = stores;
			this.isHrManager = hrIsHrManager(this.loginService.loggeduser, users);		// la base fait foi (vieux JWT sans roles)
			this.dataSource = new MatTableDataSource(employees.map(e => this.toRow(e, users, stores, schedules, resolved)));
			this.dataSource.filterPredicate = (row, filter) => this.matchRow(row, JSON.parse(filter));
			this.updateFilter();
			this.loaded = true;
		});
	}

	private toRow(e: HrEmployee, users: User[], stores: Store[], schedules: HrSchedule[], activities: HrActivity[]): EmployeeRow {
		const sch = schedules.find(s => s.id === e.id_schedule);
		const activity = sch ? (activities.find(a => a.id === hrScheduleActivityId(sch))?.name ?? '') : '';
		// R8 — seule une fiche DÉSACTIVÉE et sortie depuis plus de 5 ans est signalée : tant que la
		// conservation court, le serveur refuserait de toute façon l'anonymisation.
		const anonymizable = e.is_active === false && hrAnonymizableSince(e.leave_date, todayStr());
		const leftLabel = e.leave_date
			? e.leave_date.slice(8, 10) + '/' + e.leave_date.slice(5, 7) + '/' + e.leave_date.slice(0, 4) : '';
		return {
			id: e.id,
			name: hrEmployeeNameOf(e, users),
			id_store: e.id_store ?? null,
			store: stores.find(s => s.id === e.id_store)?.name ?? '',
			schedule: sch ? (sch.name + (activity ? ' (' + activity + ')' : '')) : '',
			hours: e.contract_hours_week != null ? e.contract_hours_week + ' h' : '',
			mobility: (e.mobility ?? []).map(id => stores.find(s => s.id === id)?.name ?? ('#' + id)).join(', '),
			active: e.is_active !== false,
			anonymizable,
			// Badge INFORMATIF : aucun écran ne déclenche encore l'anonymisation — elle existe côté
			// serveur (event dédié, irréversible : l'identité disparaît, les faits datés restent).
			anonymizableTip: anonymizable
				? 'Sortie le ' + leftLabel + ' : les 5 ans de conservation sont écoulés (art. 5(1)(e) RGPD). '
					+ 'Cette fiche peut être anonymisée — opération serveur irréversible : l\'identité disparaît, '
					+ 'les faits datés (heures, absences) restent. Aucune action automatique.'
				: '',
		};
	}

	applyFilter(event: Event): void {
		this.search.set((event.target as HTMLInputElement).value.trim().toLowerCase());
		this.updateFilter();
	}

	onActiveToggle(checked: boolean): void { this.activeOnly.set(checked); this.updateFilter(); }
	onStoreFilter(id: number | null): void { this.storeFilter.set(id); this.updateFilter(); }

	// Un seul predicate combine statut + établissement + recherche texte.
	private matchRow(row: EmployeeRow, f: { text: string; activeOnly: boolean; store: number | null }): boolean {
		if (f.activeOnly && !row.active) return false;
		if (f.store != null && row.id_store !== f.store) return false;
		if (f.text) {
			const hay = (row.name + ' ' + row.store + ' ' + row.schedule + ' ' + row.mobility).toLowerCase();
			if (!hay.includes(f.text)) return false;
		}
		return true;
	}

	private updateFilter(): void {
		this.dataSource.filter = JSON.stringify({ text: this.search(), activeOnly: this.activeOnly(), store: this.storeFilter() });
	}

	onClickRow(row: EmployeeRow): void {
		this.router.navigate(['/hr/employee/' + row.id]);
	}

	onNew(): void {
		this.router.navigate(['/hr/employee/0']);
	}
}
